import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRinging, Plus, Check, Trash, ArrowSquareOut, Phone, ArrowBendUpRight, CurrencyEur, Gavel, Eye, EyeSlash, Lightning, ClockClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYP_META = {
  versicherung_anrufen: { icon: Phone, label: "Anruf" },
  erinnerung: { icon: ArrowBendUpRight, label: "Erinnerung" },
  zahlung_pruefen: { icon: CurrencyEur, label: "Zahlung" },
  klage_pruefen: { icon: Gavel, label: "Klage" },
  manuell: { icon: BellRinging, label: "Manuell" },
};
const fmtD = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const relTage = (t) => (t == null ? "" : t < 0 ? `${-t} Tg. überfällig` : t === 0 ? "heute" : t === 1 ? "morgen" : `in ${t} Tg.`);
const GROUPS = [["ueberfaellig", "Überfällig"], ["heute", "Heute"], ["offen", "Kommende"], ["erledigt", "Erledigt"]];

function WvCard({ w, onDone, onSnooze, onToggleSichtbar, onDelete }) {
  const meta = TYP_META[w.typ] || TYP_META.manuell;
  const Icon = meta.icon;
  const tone = w.erledigt ? "border-[#E5E7EB] bg-[#FAFAFA]" : w.status === "ueberfaellig" ? "border-[#D32F2F] bg-[#FEF2F2]" : w.status === "heute" ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-[#E5E7EB] bg-white";
  return (
    <div className={`border rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3 ${tone}`} data-testid={`wv-${w.id}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${w.erledigt ? "bg-[#E5E7EB] text-[#9CA3AF]" : "bg-[#0EA5E9]/10 text-[#0369A1]"}`}><Icon size={18} weight="duotone" /></span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`font-medium ${w.erledigt ? "line-through text-[#9CA3AF]" : "text-[#0A192F]"}`}>{w.titel}</p>
            {w.quelle === "auto" && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-[#1A2238]/10 text-[#1A2238]"><Lightning size={10} weight="fill" /> automatisch</span>}
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">{meta.label}</span>
          </div>
          <p className="text-xs text-[#6B7280] mt-0.5 font-mono">{fmtD(w.faellig_am)} · {w.erledigt ? (w.erledigt_grund || "erledigt") : relTage(w.tage)}</p>
          {w.notiz && <p className="text-xs text-[#4B5563] mt-0.5">{w.notiz}</p>}
          {w.akte && (
            <Link to={`/app/akten/${w.akte.id}`} className="inline-flex items-center gap-1 text-xs text-[#1A2238] hover:underline mt-1" data-testid={`wv-akte-${w.id}`}>
              <ArrowSquareOut size={12} /> {w.akte.aktenzeichen} · {w.akte.mandant_name || "—"}
            </Link>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap md:justify-end">
        <button type="button" onClick={() => onToggleSichtbar(w)} title={w.mandant_sichtbar ? "Für Mandant sichtbar" : "Nur intern"} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ${w.mandant_sichtbar ? "border-[#16A34A]/40 bg-[#16A34A]/10 text-[#15803D]" : "border-[#E5E7EB] text-[#6B7280]"}`} data-testid={`wv-sichtbar-${w.id}`}>
          {w.mandant_sichtbar ? <Eye size={12} /> : <EyeSlash size={12} />} {w.mandant_sichtbar ? "Mandant sieht es" : "intern"}
        </button>
        {!w.erledigt && (
          <>
            <Button variant="outline" size="sm" className="h-8" onClick={() => onSnooze(w, 1)} data-testid={`wv-snooze1-${w.id}`}><ClockClockwise size={13} className="mr-1" /> +1 Tag</Button>
            <Button variant="outline" size="sm" className="h-8" onClick={() => onSnooze(w, 7)} data-testid={`wv-snooze7-${w.id}`}>+1 Woche</Button>
            <Button size="sm" className="h-8 bg-[#16A34A] hover:bg-[#15803D]" onClick={() => onDone(w)} data-testid={`wv-done-${w.id}`}><Check size={14} className="mr-1" /> Erledigt</Button>
          </>
        )}
        {w.erledigt && <Button variant="ghost" size="sm" className="h-8" onClick={() => onDone(w)} data-testid={`wv-reopen-${w.id}`}>Wieder öffnen</Button>}
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onDelete(w)} data-testid={`wv-delete-${w.id}`}><Trash size={14} /></Button>
      </div>
    </div>
  );
}

export default function Wiedervorlagen() {
  const { user, setUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [akten, setAkten] = useState([]);
  const [tab, setTab] = useState("offen");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ akte_id: "", titel: "", faellig_am: "", notiz: "", mandant_sichtbar: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [w, a] = await Promise.all([api.get("/wiedervorlagen"), api.get("/akten")]);
      setRows(w.data); setAkten(a.data);
    } catch (err) { toast.error(errMsg(err, "Wiedervorlagen konnten nicht geladen werden")); }
  };
  useEffect(() => { load(); }, []);

  const done = async (w) => { try { await api.patch(`/wiedervorlagen/${w.id}`, { erledigt: !w.erledigt }); load(); } catch (err) { toast.error(errMsg(err, "Fehler")); } };
  const snooze = async (w, tage) => { try { await api.post(`/wiedervorlagen/${w.id}/verschieben`, { tage }); toast.success(`Verschoben um ${tage} Tag${tage > 1 ? "e" : ""}`); load(); } catch (err) { toast.error(errMsg(err, "Verschieben fehlgeschlagen")); } };
  const toggleSichtbar = async (w) => { try { await api.patch(`/wiedervorlagen/${w.id}`, { mandant_sichtbar: !w.mandant_sichtbar }); load(); } catch (err) { toast.error(errMsg(err, "Fehler")); } };
  const del = async (w) => { if (!window.confirm("Wiedervorlage löschen?")) return; try { await api.delete(`/wiedervorlagen/${w.id}`); load(); } catch (err) { toast.error(errMsg(err, "Löschen fehlgeschlagen")); } };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/wiedervorlagen", form);
      toast.success("Wiedervorlage angelegt");
      setOpen(false); setForm({ akte_id: "", titel: "", faellig_am: "", notiz: "", mandant_sichtbar: false }); load();
    } catch (err) { toast.error(errMsg(err, "Anlegen fehlgeschlagen")); } finally { setSaving(false); }
  };

  const saveSetting = async (patch) => {
    try {
      const r = await api.patch("/auth/me", patch);
      setUser(r.data);
      toast.success("Einstellung gespeichert");
    } catch (err) { toast.error(errMsg(err, "Einstellung konnte nicht gespeichert werden")); }
  };

  const grouped = useMemo(() => {
    const g = { ueberfaellig: [], heute: [], offen: [], erledigt: [] };
    rows.forEach(r => (g[r.status] || g.offen).push(r));
    return g;
  }, [rows]);
  const offenCount = grouped.ueberfaellig.length + grouped.heute.length + grouped.offen.length;
  const shown = tab === "offen" ? GROUPS.slice(0, 3) : GROUPS.slice(3);

  return (
    <div className="space-y-8" data-testid="wiedervorlagen-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2">Wiedervorlage</p>
          <h1 className="font-serif text-4xl tracking-tight">{offenCount} offene Wiedervorlagen</h1>
          <p className="text-sm text-[#6B7280] mt-2">
            {grouped.ueberfaellig.length} überfällig · {grouped.heute.length} heute · Kette nach Versand: +1 Tag anrufen → +7 Erinnerung → +14 Zahlung prüfen → +28 Klage prüfen
          </p>
        </div>
        <Button className="bg-[#1A2238] hover:bg-[#2C3859]" onClick={() => setOpen(true)} data-testid="new-wv-btn"><Plus size={16} className="mr-2" /> Wiedervorlage</Button>
      </header>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wv-settings">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium flex items-center gap-1.5"><Lightning size={14} weight="fill" className="text-[#F59E0B]" /> Automatische Wiedervorlagen</p>
            <p className="text-xs text-[#6B7280] mt-0.5">Nach jedem versendeten Schriftsatz wird die Kette automatisch angelegt. Pro Akte in der Aktenansicht abschaltbar.</p>
          </div>
          <Switch checked={user?.wiedervorlage_auto !== false} onCheckedChange={v => saveSetting({ wiedervorlage_auto: v })} data-testid="wv-setting-auto" />
        </div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium flex items-center gap-1.5"><Eye size={14} className="text-[#16A34A]" /> Standard: für Mandanten sichtbar</p>
            <p className="text-xs text-[#6B7280] mt-0.5">Neue automatische Wiedervorlagen erscheinen im Mandantenportal. Pro Eintrag jederzeit umschaltbar.</p>
          </div>
          <Switch checked={!!user?.wiedervorlage_mandant_sichtbar} onCheckedChange={v => saveSetting({ wiedervorlage_mandant_sichtbar: v })} data-testid="wv-setting-sichtbar" />
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5">
        <button onClick={() => setTab("offen")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${tab === "offen" ? "bg-[#1A2238] text-white" : "text-[#4B5563]"}`} data-testid="wv-tab-offen">Offen ({offenCount})</button>
        <button onClick={() => setTab("erledigt")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${tab === "erledigt" ? "bg-[#1A2238] text-white" : "text-[#4B5563]"}`} data-testid="wv-tab-erledigt">Erledigt ({grouped.erledigt.length})</button>
      </div>

      {offenCount === 0 && tab === "offen" && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center" data-testid="wv-empty">
          <BellRinging size={40} weight="duotone" className="mx-auto text-[#9CA3AF]" />
          <p className="font-serif text-2xl mt-4">Keine offenen Wiedervorlagen</p>
          <p className="text-sm text-[#6B7280] mt-1">Sobald du einen Schriftsatz versendest, legt LexAuto die Kette automatisch an.</p>
        </div>
      )}
      {shown.filter(([k]) => grouped[k].length).map(([k, label]) => (
        <section key={k} data-testid={`wv-gruppe-${k}`}>
          <h2 className="text-xs uppercase tracking-wide text-[#6B7280] mb-2">{label} <span className="font-mono">({grouped[k].length})</span></h2>
          <div className="space-y-2">{grouped[k].map(w => <WvCard key={w.id} w={w} onDone={done} onSnooze={snooze} onToggleSichtbar={toggleSichtbar} onDelete={del} />)}</div>
        </section>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Neue Wiedervorlage</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="overline">Akte</Label>
              <Select value={form.akte_id} onValueChange={v => setForm(f => ({ ...f, akte_id: v }))}>
                <SelectTrigger data-testid="wv-form-akte"><SelectValue placeholder="Akte wählen…" /></SelectTrigger>
                <SelectContent>{akten.map(a => <SelectItem key={a.id} value={a.id}>{a.aktenzeichen} · {a.mandant_name || "—"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Was ist zu tun?</Label>
              <Input required value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} placeholder="z. B. Sachbearbeiter zurückrufen" data-testid="wv-form-titel" />
            </div>
            <div>
              <Label className="overline">Fällig am</Label>
              <Input required type="date" value={form.faellig_am} onChange={e => setForm(f => ({ ...f, faellig_am: e.target.value }))} data-testid="wv-form-datum" />
            </div>
            <div>
              <Label className="overline">Notiz</Label>
              <Textarea rows={2} value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))} data-testid="wv-form-notiz" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Für Mandanten sichtbar</Label>
              <Switch checked={form.mandant_sichtbar} onCheckedChange={v => setForm(f => ({ ...f, mandant_sichtbar: v }))} data-testid="wv-form-sichtbar" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving || !form.akte_id} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="wv-form-submit">{saving ? "Speichert…" : "Anlegen"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
